import { IconArrowRightTop } from "@synthesis/shared-icons";
import { Button, ScrollArea } from "@synthesis/shared-ui";
import { cn } from "@synthesis/shared-utils";
import { Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import { ApplicationDeadlineControlBadge } from "../../../components/application/application-deadline-control-badge";
import { useUserRoles } from "../../../shared/hooks/use-user-roles";
import {
  formatMonitoringAmount,
  formatMonitoringDeadlineLabel,
  formatMonitoringNumber,
  formatMonitoringPercent,
  mapMonitoringApplication,
  type MonitoringMetricItem,
} from "../monitoring-page.lib";
import { useMonitoringApplications } from "../hooks/use-monitoring-applications";

type MonitoringMetricPopoverProps = {
  metric: MonitoringMetricItem;
  enabled: boolean;
};

const POPOVER_LIST_MAX_HEIGHT_CLASS = "max-h-[18rem]";

export const MonitoringMetricPopover = ({ metric, enabled }: MonitoringMetricPopoverProps) => {
  const navigate = useNavigate();
  const roles = useUserRoles();
  const isApplicationsDestination = roles.isInternalNtpChief || roles.isInternalApparatusNg;
  const details = useMonitoringApplications(metric, enabled);
  const viewportRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLLIElement>(null);
  const { fetchNextPage, hasNextPage, isFetchingNextPage, isFetchNextPageError } = details;

  useEffect(() => {
    if (!enabled || !hasNextPage || isFetchingNextPage || isFetchNextPageError || !loadMoreRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) void fetchNextPage();
      },
      { root: viewportRef.current },
    );
    observer.observe(loadMoreRef.current);
    return () => observer.disconnect();
  }, [enabled, fetchNextPage, hasNextPage, isFetchingNextPage, isFetchNextPageError]);

  const applications = (details.data?.pages.flatMap((page) => page.content ?? []) ?? []).flatMap((item) => {
    const application = mapMonitoringApplication(item, metric.id);
    return application ? [application] : [];
  });
  const hasDetailColumn = applications.some((item) => item.deadlineState != null || item.amount != null);

  return (
    <div className="flex w-full flex-col">
      <div className="px-6 pb-2 pt-6">
        <p className="subtitle-lg text-type-common">
          {metric.label}: {formatMonitoringNumber(metric.value)} <span className="text-type-light">({formatMonitoringPercent(metric.percent)})</span>
        </p>
      </div>

      <ScrollArea ref={viewportRef} direction="vertical" rootClassName={POPOVER_LIST_MAX_HEIGHT_CLASS} className={POPOVER_LIST_MAX_HEIGHT_CLASS}>
        {details.isPending ? (
          <p role="status" className="paragraph-base text-type-minor px-6 py-4">
            Загрузка заявок…
          </p>
        ) : details.isError && !details.data ? (
          <div role="alert" className="flex items-center gap-4 px-6 py-4">
            <p className="paragraph-base text-type-error">Не удалось загрузить заявки</p>
            <Button type="button" theme="secondary" size="sm" onClick={() => void details.refetch()}>
              Повторить
            </Button>
          </div>
        ) : applications.length === 0 ? (
          <p className="paragraph-base text-type-minor px-6 py-4">Заявок нет</p>
        ) : null}
        <ul>
          {applications.map((item) => (
            <li key={item.id}>
              <Link
                to="/applications/$id"
                params={{ id: item.id }}
                className={cn(
                  "border-neutrals-90 hover:bg-tertiary-99 grid h-12 items-center border-b px-4",
                  hasDetailColumn ? "grid-cols-[6.75rem_minmax(0,1fr)_11.5rem_12rem]" : "grid-cols-[6.75rem_minmax(0,1fr)_11.5rem]",
                )}
              >
                <p className="paragraph-base text-type-common">{item.number}</p>
                <p className="paragraph-base text-type-common truncate pr-4" title={item.title}>
                  {item.title}
                </p>
                <p className="paragraph-base text-type-common truncate pr-4" title={item.railwayName}>
                  {item.railwayName}
                </p>
                {hasDetailColumn ? (
                  <div className="flex justify-end">
                    {item.deadlineState != null && item.remainingDays != null ? (
                      <ApplicationDeadlineControlBadge state={item.deadlineState} label={formatMonitoringDeadlineLabel(item.remainingDays)} />
                    ) : item.amount != null ? (
                      <p className="paragraph-base text-type-common whitespace-nowrap">{formatMonitoringAmount(item.amount)}</p>
                    ) : null}
                  </div>
                ) : null}
              </Link>
            </li>
          ))}
          {hasNextPage ? (
            <li ref={loadMoreRef} className="px-6 py-2">
              {isFetchNextPageError ? (
                <div role="alert" className="flex items-center gap-4">
                  <p className="paragraph-base text-type-error">Не удалось загрузить остальные заявки</p>
                  <Button theme="secondary" size="sm" onClick={() => void fetchNextPage()}>
                    Повторить
                  </Button>
                </div>
              ) : (
                <p role="status" className="paragraph-base text-type-minor">
                  Загрузка заявок…
                </p>
              )}
            </li>
          ) : null}
        </ul>
      </ScrollArea>

      <div className="px-6 py-2">
        <Button
          type="button"
          theme="text"
          size="sm"
          leftIcon={<IconArrowRightTop className="size-5" aria-hidden />}
          onClick={() => navigate({ to: "/", search: { monitoringMetric: metric.id, railway: metric.railwayId } })}
        >
          {isApplicationsDestination ? "Открыть в заявках" : "Открыть в реестре"}
        </Button>
      </div>
    </div>
  );
};
